/* "Details behind every number": slide-in panel + clickable numbers used across the Lottery module */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Btn } from "@/components/gull/ui";
import { Link, useNavigate } from "react-router-dom";
import { X, ArrowLeft, ChevronRight, Loader2 } from "lucide-react";
import { money, errMsg, useLegacyApi } from "@/hooks/useLegacy";
import { FixItButton } from "@/components/gull/FixIt";

const DrillCtx = createContext(null);
const ReloadCtx = createContext(() => {});
export const useDrill = () => useContext(DrillCtx);

/* spec: { title, subtitle, detail } or { title, subtitle, load: () => Promise<detail> }
   detail: { title, subtitle, summary:[{label,value,money}], note, sections:[{title, columns:[{key,label,type}], rows, totals, empty}] }
   row.__drill (optional) = nested spec opened when the row is clicked */
export const DrillProvider = ({ children }) => {
  const [stack, setStack] = useState([]);
  const open = useCallback((spec) => setStack((s) => [...s, { ...spec, key: Date.now() }]), []);
  const back = useCallback(() => setStack((s) => s.slice(0, -1)), []);
  const close = useCallback(() => setStack([]), []);
  const value = useMemo(() => ({ open, close }), [open, close]);
  return (
    <DrillCtx.Provider value={value}>
      {children}
      <DrillPanel stack={stack} back={back} close={close} open={open} />
    </DrillCtx.Provider>
  );
};

/* Clickable number / label. Renders children with a dashed underline; opens the drill on click. */
export const Drillable = ({ spec, children, className = "", testId, block, tile, style }) => {
  const drill = useDrill();
  if (!drill || !spec) return <span className={className} style={style}>{children}</span>;
  const base = tile ? "flex flex-col items-start hover:shadow-md hover:ring-1 hover:ring-[#663399]/30" : block ? "flex w-full justify-between items-center gap-1 hover:bg-[#663399]/5 -mx-1 px-1 hover:text-[#663399]" : "inline-flex items-center gap-1 hover:underline hover:text-[#663399]";
  return (
    <button type="button" data-testid={testId} style={style} onClick={(e) => { e.stopPropagation(); drill.open(typeof spec === "function" ? spec() : spec); }} title="Click for details"
      className={`${base} text-left rounded transition-all decoration-dashed underline-offset-[3px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#663399]/30 cursor-pointer ${className}`}>
      {children}
    </button>
  );
};

/* Row-level quick action (e.g. "Mark sold") – calls the store API, then reloads the open panel */
const ActionCell = ({ a, row }) => {
  const api = useLegacyApi();
  const reload = useContext(ReloadCtx);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await api[a.method](a.path); toast.success(a.success || "Done"); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <Btn size="sm" color={a.tone || "primary"} loading={busy} data-testid={`drill-action-${row.Game_Activation_ID ?? row.__key ?? a.label}`} onClick={(e) => { e.stopPropagation(); run(); }}>{a.label}</Btn>;
};

/* Customer name that opens the customer's credit history (balance owed shown next to it when provided) */
export const CustomerLink = ({ id, name, balance, testId, className = "" }) => {
  const api = useLegacyApi();
  const spec = () => ({ title: name, subtitle: "Customer credit history", load: () => api.get(`/customers/${id}/drill`) });
  return (
    <Drillable spec={spec} testId={testId || `customer-link-${id}`} className={`font-semibold ${className}`}>
      {name}{balance !== undefined && <span className={`ml-1.5 rounded-full px-1.5 py-px text-[11px] font-bold tabular-nums ${Number(balance) > 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>{money(balance)}</span>}
    </Drillable>
  );
};

const fmt = (v, type) => {
  if (v === null || v === undefined || v === "") return "";
  if (type === "money") return money(v);
  if (type === "int") return Number(v).toLocaleString("en-US");
  return String(v);
};

const Cell = ({ row, c, close }) => {
  const v = row[c.key];
  if (c.type === "book" && row.Game_Inventory_ID) return <Link data-testid={`drill-book-${row.Game_Inventory_ID}`} to={`/lotto-book/${row.Game_Inventory_ID}`} onClick={close} className="text-[#663399] font-semibold hover:underline">{v}</Link>;
  if (c.type === "action" && v) return <ActionCell a={v} row={row} />;
  if (c.type === "customer" && row.customer_detail_id) return <CustomerLink id={row.customer_detail_id} name={v} balance={row.Balance} />;
  if (c.type === "bar") return <div className="flex items-center gap-2"><div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#663399]" style={{ width: `${Math.min(100, Math.max(0, Number(v) || 0))}%` }} /></div><span className="text-xs tabular-nums">{Number(v || 0).toFixed(1)}%</span></div>;
  const num = c.type === "money" || c.type === "int";
  return <span className={`${num ? "tabular-nums" : ""} ${c.type === "money" && Number(v) < 0 ? "text-red-600" : ""}`}>{fmt(v, c.type)}</span>;
};

const Section = ({ s, close, open }) => {
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? s.rows : s.rows.slice(0, 200);
  const hasTotals = s.totals && Object.keys(s.totals).length > 0;
  return (
    <div className="mb-5" data-testid={`drill-section-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">{s.title} <span className="text-gray-400 font-semibold">({s.rows.length})</span></div>
      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="w-full text-sm text-gray-700">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-600">
            <tr>{s.columns.map((c) => <th key={c.key} className={`px-3 py-2 font-bold border-b border-gray-200 whitespace-nowrap ${c.type === "money" || c.type === "int" ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={s.columns.length} className="px-3 py-5 text-center text-gray-400">{s.empty || "No records"}</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.__key ?? i} onClick={r.__drill ? () => open(r.__drill) : undefined} className={`border-b border-gray-100 ${i % 2 ? "bg-gray-50/60" : "bg-white"} ${r.__drill ? "cursor-pointer hover:bg-[#663399]/5" : ""}`}>
                {s.columns.map((c) => <td key={c.key} className={`px-3 py-1.5 ${c.type === "wrap" ? "whitespace-normal text-xs text-gray-600 min-w-[180px] max-w-md leading-snug" : "whitespace-nowrap"} ${c.type === "money" || c.type === "int" ? "text-right" : ""}`}><Cell row={r} c={c} close={close} /></td>)}
              </tr>
            ))}
          </tbody>
          {hasTotals && rows.length > 0 && (
            <tfoot><tr className="bg-gray-50 font-bold">{s.columns.map((c, i) => <td key={c.key} className={`px-3 py-2 ${c.type === "money" || c.type === "int" ? "text-right tabular-nums" : ""}`}>{i === 0 && s.totals[c.key] === undefined ? "Total" : fmt(s.totals[c.key], c.type === "bar" ? "text" : c.type)}</td>)}</tr></tfoot>
          )}
        </table>
      </div>
      {s.rows.length > 200 && !showAll && <button className="text-xs text-[#663399] font-semibold mt-1" onClick={() => setShowAll(true)}>Show all {s.rows.length} rows</button>}
    </div>
  );
};

const DrillPanel = ({ stack, back, close, open }) => {
  const top = stack[stack.length - 1];
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);
  const [ver, setVer] = useState({ top: null, n: 0 });
  const version = ver.top === top ? ver.n : 0;
  const reload = useCallback(() => setVer((v) => ({ top, n: (v.top === top ? v.n : 0) + 1 })), [top]);
  const navigate = useNavigate();
  useEffect(() => {
    setErr(null);
    if (!top) { setDetail(null); return; }
    if (top.detail) { setDetail(top.detail); return; }
    if (!version) setDetail(null);
    let alive = true;
    Promise.resolve(top.load()).then((d) => alive && setDetail(d)).catch((e) => alive && setErr(errMsg(e)));
    return () => { alive = false; };
  }, [top, version]);
  useEffect(() => {
    if (!top) return;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [top, close]);
  const openLink = (to) => { close(); navigate(to); };
  return (
    <div className={`fixed inset-0 z-[60] print:hidden ${top ? "" : "pointer-events-none"}`} aria-hidden={!top}>
      <div onClick={close} className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${top ? "opacity-100" : "opacity-0"}`} />
      <aside data-testid="drill-panel" className={`absolute right-0 top-0 h-full w-full lg:w-[92vw] xl:w-[85vw] max-w-[1500px] bg-[#f8f8fb] shadow-2xl flex flex-col transition-transform duration-300 ease-out ${top ? "translate-x-0" : "translate-x-full"}`} style={{ fontFamily: "Nunito, sans-serif" }}>
        {top && (
          <>
            <div className="flex items-start gap-3 px-6 py-4 bg-white border-b border-gray-200">
              {stack.length > 1 && <button data-testid="drill-back" onClick={back} className="mt-1 rounded-full p-1.5 text-gray-500 hover:bg-gray-100"><ArrowLeft className="w-4 h-4" /></button>}
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-[#663399] font-bold">Details behind the number</div>
                <h2 data-testid="drill-title" className="text-lg font-extrabold text-gray-800 leading-tight">{detail?.title || top.title}</h2>
                {(detail?.subtitle || top.subtitle) && <div data-testid="drill-subtitle" className="text-sm text-gray-500">{detail?.subtitle || top.subtitle}</div>}
              </div>
              <button data-testid="drill-close" onClick={close} className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 md:px-8 py-5">
              {err && <div data-testid="drill-error" className="rounded border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">{err}</div>}
              {!detail && !err && <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />Loading details…</div>}
              {detail && (
                <>
                  {detail.summary?.length > 0 && (
                    <div data-testid="drill-summary" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
                      {detail.summary.map((t) => { const s = t.money ? money(t.value) : String(t.value ?? ""); return <div key={t.label} className="bg-white rounded-lg shadow-sm px-4 py-3"><div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{t.label}</div><div className={`font-extrabold leading-tight ${s.length > 14 ? "text-sm" : "text-lg"} ${t.money && Number(t.value) < 0 ? "text-red-600" : "text-gray-800"}`}>{s}</div></div>; })}
                    </div>
                  )}
                  {detail.note && <p data-testid="drill-note" className="text-xs text-gray-600 bg-white border-l-4 border-[#663399]/40 rounded px-3 py-2 mb-4">{detail.note}</p>}
                  {detail.fixit && <div className="mb-4 flex justify-end"><FixItButton shiftId={detail.fixit} onChanged={reload} label="Fix it" /></div>}
                  {detail.links?.length > 0 && <div className="flex flex-wrap gap-2 mb-4">{detail.links.map((l) => <button key={l.to} onClick={() => openLink(l.to)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#663399] bg-white border border-[#663399]/30 rounded-full px-3 py-1 hover:bg-[#663399] hover:text-white">{l.label}<ChevronRight className="w-3 h-3" /></button>)}</div>}
                  <ReloadCtx.Provider value={reload}>{(detail.sections || []).map((s, i) => <Section key={i} s={s} close={close} open={open} />)}</ReloadCtx.Provider>
                </>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

/* helpers to build client-side details */
export const colDef = (key, label, type = "text") => ({ key, label, type });
export const sectionDef = (title, columns, rows, totals = {}, empty) => ({ title, columns, rows, totals, empty });
